// DocumentPreviewModal.tsx
//
// One modal for looking at a knowledge-base document, whichever way it was
// opened: a row in the KB table, or a `kbdoc:` citation in a chat answer.
//
// It was two, and they disagreed. The table opened the file — which meant a
// browser frame for a PDF and, for everything else, the dead end "this file type
// cannot be shown here" plus a download button. Citations opened the extraction
// instead, with the schema above it for a queried document. So the same
// spreadsheet showed its sheets, columns and joins when reached from an answer
// and nothing at all when reached from the library, and the reader had no way to
// get from one view to the other.
//
// Now every entry point lands here, and one rule decides what it opens on: the
// original file when the browser can draw it, the extraction when it cannot.
//
//   * a PDF opens as a PDF, at the cited page — a citation into page 429 of an
//     850-page manual wants that page, not a paragraph of its text;
//   * an image opens as the image;
//   * a workbook, a Word file, a deck, a PLC export open on the extraction —
//     the text Alpinist read, with the schema above it when the document is
//     queried rather than read;
//   * both views stay one click apart in the header, and the "original file"
//     view of something undrawable says so and offers the download.
//
// A citation carries an id and no MIME type, so what the file is cannot be known
// before asking for it: those open on the file, and fall back to the extraction
// once the bytes say the browser cannot draw them. A caller that knows the type
// — a library row, a chat attachment — decides without the round trip, so a
// spreadsheet opened from the library never fetches bytes it cannot use.
import type {FC, SyntheticEvent} from 'react'
import {useEffect, useState} from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

import BasicModal from '@shared/components/BasicModal'
import {saveBlob} from '@shared/utils/saveBlob'
import UnitContent from '@shared/components/documentUnit/UnitContent'
import DocumentSchemaPanel from '@shared/components/documentUnit/DocumentSchemaPanel'
import {canRenderInline} from '@shared/components/documentUnit/renderable'
import {shortUnitLabel} from '@shared/components/documentUnit/parseUnitContent'
import {
  useGetDocumentSchemaQuery,
  useLazyPreviewDocumentQuery,
  usePreviewDocumentQuery,
  useReadDocumentUnitQuery,
} from '@apis/aiCoreAPI'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'

type View = 'extract' | 'file'

interface DocumentPreviewModalProps {
  open: boolean
  onClose: () => void
  documentId: string | null
  /** Fallback heading until the response says what the document is called. */
  title?: string
  maxWidth?: string | undefined
  width?: string | undefined
  height?: string | number
  /**
   * Unit to open at, from a citation's `kbdoc:…?page=X` — a sheet as often as a
   * page. Also the page a rendered PDF jumps to.
   */
  page?: number
  /**
   * Canonical MIME type of the document, when the caller tracks it. Undefined is
   * not an assumption any more: the fetched blob carries its own type, and that
   * is what decides (AB#5992).
   */
  mimeType?: string
  /** Filename to save as when the file is downloaded. */
  filename?: string
}

const DocumentPreviewModal: FC<DocumentPreviewModalProps> = ({
  open,
  onClose,
  documentId,
  title,
  maxWidth = '70vw',
  width = '70vw',
  height = '70vh',
  page,
  mimeType,
  filename,
}) => {
  const [t] = useCustomTranslation('alpinist')
  const [showNotification] = useSnackbar()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  // null = follow the default for this document. A reader who switches views
  // stays switched, until a different document is opened.
  const [chosenView, setChosenView] = useState<View | null>(null)

  useEffect(() => {
    setChosenView(null)
  }, [documentId])

  // Unknown means "ask": a citation has no MIME type, and guessing the
  // extraction for it is what sent PDF citations to a paragraph of text instead
  // of the page they pointed at.
  const opensOnFile = mimeType === undefined || canRenderInline(mimeType)
  const view: View = chosenView ?? (opensOnFile ? 'file' : 'extract')
  const active = open && Boolean(documentId)

  // `currentData`, not `data`, on all three queries below. RTK Query's `data` is
  // documented as "the latest returned result regardless of hook arg" — so when
  // this modal is reused for the next document, it keeps handing back the
  // previous document's response until the new one resolves. That is how a
  // workbook's sheets, columns and joins came to be drawn under a PDF's title,
  // next to the PDF's own correct text. `currentData` is the same value scoped
  // to the current argument.
  const {
    currentData: unitData,
    isFetching: unitLoading,
    isError: unitError,
  } = useReadDocumentUnitQuery(
    // A citation names its unit; a row in the library names none, and "the
    // document" then means where it starts. The endpoint requires the unit to be
    // named — deliberately, so the agent cannot read an arbitrary one and take
    // it for the whole file — so the default belongs here, at the one caller
    // that has a document rather than a passage.
    {id: documentId as string, number: page ?? 1},
    // Not gated on the view. Besides feeding the extraction it is what carries
    // the document's real title, and the file view needs that too: a citation's
    // fallback label is whatever text the answer linked, which for a machine-
    // written citation is sometimes the document's raw id.
    {skip: !active},
  )

  // A queried document has a schema; a PDF does not, and answers 409. Asked for
  // without knowing which kind this is, because that 409 is cheaper than
  // threading the document's nature through every citation — but only in the
  // view that shows it, so a PDF read as a PDF never asks at all.
  const {currentData: schemaData} = useGetDocumentSchemaQuery(
    {id: documentId as string},
    {skip: !active || view !== 'extract'},
  )

  const {
    currentData: fileBlob,
    isFetching: fileLoading,
    isError: fileError,
  } = usePreviewDocumentQuery({id: documentId as string}, {skip: !active || view !== 'file'})

  // And checked against the document they name, which `currentData` alone does
  // not do: each view is fed by queries the other one skips, and a skipped
  // query is not a cleared one either. Both responses carry the id, so both say
  // whether they belong here — the blob cannot, which is the whole reason the
  // queries above are scoped by argument rather than only guarded here.
  const unit = unitData?.document_id === documentId ? unitData : undefined
  const schema = schemaData?.document_id === documentId ? schemaData : undefined

  const [fetchFile] = useLazyPreviewDocumentQuery()

  // Only for a caller that had no MIME type to offer, and only while the reader
  // has not chosen a view themselves: "original file" asked for explicitly must
  // stay on the file and say why it cannot be shown.
  useEffect(() => {
    if (chosenView === null && fileBlob && !canRenderInline(mimeType ?? fileBlob.type)) {
      setChosenView('extract')
    }
  }, [chosenView, fileBlob, mimeType])

  useEffect(() => {
    if (!fileBlob) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(fileBlob)
    setPreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [fileBlob])

  if (!open || !documentId) return null

  // The stored title first, the caller's label second. The label is a fallback,
  // not an authority: a citation's is the answer's own link text, which is a raw
  // document id often enough to matter. Staleness across documents is handled
  // where it belongs — by `currentData` and the id check above — not by
  // preferring a worse name.
  const documentTitle = unit?.document_title || title || t('kb.preview.title_default')
  // Saved under a real name even before anything has loaded, so a download from
  // the error arm does not land as "Document preview".
  const downloadName = filename || unit?.document_title || title || 'document'

  const download = async (): Promise<void> => {
    setDownloading(true)
    try {
      const blob = await fetchFile({id: documentId}).unwrap()
      saveBlob(blob, downloadName)
    } catch (_error) {
      showNotification({message: t('kb.preview.download_failed'), severity: 'error'})
    } finally {
      setDownloading(false)
    }
  }

  // The same name the citation used, so the heading matches the link that was
  // clicked: "Kreis 1", not "sheet 1" and not the locator's row range. Only in
  // the extract view — the file view is the whole file, not one unit of it.
  const heading =
    view === 'extract' && unit
      ? `${unit.document_title} – ${shortUnitLabel(unit.label) || `${unit.unit} ${unit.number ?? 1}`}`
      : documentTitle

  const switcher = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={view}
      onChange={(_event: SyntheticEvent, next: View | null) => next && setChosenView(next)}
      aria-label={t('kb.preview.view_label')}
    >
      <ToggleButton value="extract">{t('kb.preview.view_extract')}</ToggleButton>
      <ToggleButton value="file">{t('kb.preview.view_file')}</ToggleButton>
    </ToggleButtonGroup>
  )

  const queried = Boolean(schema && schema.tables.length > 0)

  // Which table the reader came for — only when the caller actually named a
  // unit. A library row names none, and the read defaults to where the document
  // starts; treating that as a citation marked sheet 1 as "the sheet cited" and
  // folded the other 23 away, which is the opposite of what opening a file from
  // the library is for.
  //
  // Matched by name against the unit's label rather than by index: `page` is the
  // table's position in the projection and the two orders do agree, but a
  // citation landing on the wrong sheet is worse than one landing on none, so
  // the index is only the fallback.
  const citedTable = ((): string | undefined => {
    if (!schema || !unit || page === undefined) return undefined
    const wanted = shortUnitLabel(unit.label)
    const byName = schema.tables.find(
      (table) => table.name === wanted || table.label === unit.label,
    )
    if (byName) return byName.name
    return unit.number ? schema.tables[unit.number - 1]?.name : undefined
  })()

  // Only known once the blob is here for a caller that had no MIME type to give.
  const inline = canRenderInline(mimeType ?? fileBlob?.type)
  const pageFragment = page && page > 0 ? `#page=${page}` : ''

  const loading = view === 'extract' ? unitLoading : fileLoading

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title={heading}
      maxWidth={maxWidth}
      width={width}
      headerAction={switcher}
    >
      <>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{minHeight: '30vh'}}>
            <CircularProgress />
          </Stack>
        )}

        {view === 'extract' && unitError && !unitLoading && (
          <Stack spacing={2} alignItems="center" sx={{minHeight: '20vh', pt: 4}}>
            <Typography variant="body2" color="error">
              {t('kb.preview.unit_unavailable')}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={downloading}
              onClick={() => void download()}
            >
              {t('kb.preview.download')}
            </Button>
          </Stack>
        )}

        {view === 'extract' && unit && !unitLoading && !unitError && (
          <Stack spacing={2}>
            <Box sx={{maxHeight: '60vh', overflow: 'auto'}}>
              {/* Two things reach a reader here, and they came from two
                  different mechanisms. Saying which is which is the difference
                  between "here is some stuff about your file" and an account of
                  how the answer was arrived at — so each section says what it
                  is, why it looks like that, and they are separated rather than
                  stacked. */}
              {queried ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" display="block">
                      {t('kb.schema.queried_heading')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                      {t('kb.schema.queried_note')}
                    </Typography>
                    <DocumentSchemaPanel schema={schema} focus={citedTable} />
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="overline" color="text.secondary" display="block">
                      {t('kb.schema.extract_heading')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                      {t('kb.schema.extract_note')}
                    </Typography>
                    <UnitContent content={unit.content} unit={unit.unit} />
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="overline" color="text.secondary" display="block">
                    {t('kb.schema.prose_heading')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('kb.schema.prose_note')}
                  </Typography>
                  <UnitContent content={unit.content} unit={unit.unit} />
                </Stack>
              )}
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {/* A unit can be the whole file — a CSV is one sheet — so the
                    reader has to know when this is a part of it. */}
                {unit.complete
                  ? t('kb.preview.extracted_note')
                  : t('kb.preview.extracted_partial', {
                      shown: unit.parts_returned,
                      total: unit.parts,
                    })}
              </Typography>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                disabled={downloading}
                onClick={() => void download()}
              >
                {t('kb.preview.download_original')}
              </Button>
            </Stack>
          </Stack>
        )}

        {view === 'file' && fileError && !fileLoading && (
          <Typography variant="body2" color="error">
            {t('kb.preview.file_unavailable')}
          </Typography>
        )}

        {view === 'file' && previewUrl && !fileLoading && !fileError && !inline && (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{minHeight: '30vh'}}>
            <Typography variant="body2" color="text.secondary">
              {t('kb.preview.unavailable')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={downloading}
              onClick={() => void download()}
            >
              {t('kb.preview.download')}
            </Button>
          </Stack>
        )}

        {view === 'file' && previewUrl && !fileLoading && !fileError && inline && (
          <Box sx={{height}}>
            <embed
              src={`${previewUrl}${pageFragment}`}
              style={{border: 0, width: '100%', height: '100%'}}
              title={documentTitle}
            />
          </Box>
        )}
      </>
    </BasicModal>
  )
}

export default DocumentPreviewModal
